import { ReactElement, useEffect } from 'react';
import { UIErrorState, UILoadingState, UIText, useUINav } from '@/ui';
import { NavLink, useParams } from 'react-router';
import { Task } from '@/api';
import {
    NoAccessMask,
    TaskDetailMapPanel,
    TaskEventsPanel,
    TaskHeader,
    TaskLastLocationPanel,
    TaskSubjectInfo,
    TaskTeamInfo
} from '@/tasks/components';
import styles from './task-id-page.module.scss';
import {
    TaskBeaconSnapshotProvider,
    TaskInfoProvider,
    TaskLocationsProvider,
    TaskProvider,
    useTask
} from '@/tasks/providers';
import { useEnableTaskSockets } from '@/tasks/queries';
import { useCompanyContext } from '@/companies/providers';
import { useCompanyPermissions } from '@/companies/hooks';
import { usePagesTranslation } from '../hooks';

export function TaskIdPage(): ReactElement {
    const { taskId } = useParams();

    const companyId = useCompanyContext().company.id;

    return (
        <>
            {taskId && (
                <TaskProvider taskId={taskId}>
                    <TaskInfoProvider taskId={taskId}>
                        <TaskLocationsProvider
                            taskId={taskId}
                            companyId={companyId}
                        >
                            <TaskIdPage2 />
                        </TaskLocationsProvider>
                    </TaskInfoProvider>
                </TaskProvider>
            )}
        </>
    );
}

function TaskIdPage2(): ReactElement {
    const { t } = usePagesTranslation();
    const { task, status, refetch } = useTask();

    const { company } = useCompanyContext();
    const { hasSubscription, hasLicense } = useCompanyPermissions();

    const { setParts } = useUINav();
    useEffect(() => {
        setParts([
            {
                text: t('shared.labels.tasks'),
                link: `/company/${company.id}/tasks`,
                state: { fromTask: true }
            },
            { text: task?.title ?? '' }
        ]);
    }, [setParts, task?.title, company.id, t]);

    return (
        <div style={{ display: 'flex', flex: 1, position: 'relative' }}>
            {status === 'pending' && <UILoadingState />}
            {status === 'error' && (
                <UIErrorState
                    title={t('pages.task_id.load_error')}
                    onRetry={refetch}
                />
            )}
            {status === 'success' && task && <TaskLoaded task={task} />}
            {!hasSubscription && <NoAccessMask withMessage="no-subscription" />}
            {hasSubscription && !hasLicense && (
                <NoAccessMask withMessage="no-license" />
            )}
        </div>
    );
}

function TaskLoaded(props: { task: Task }): ReactElement {
    const { task } = props;
    const { t } = usePagesTranslation();

    useEnableTaskSockets(task.id, task.companyId);

    return (
        <div className={styles.contentContainer}>
            <div className={styles.contentContainer2}>
                <div className={styles.backButtonContainer}>
                    <NavLink to={'../tasks'} state={{ fromTask: true }}>
                        <UIText type="link" underline>
                            {t('pages.task_id.back_to_tasks')}
                        </UIText>
                    </NavLink>
                </div>
                <div className={styles.contentContainer3}>
                    <TaskHeader task={task} detached simple={false} />
                    <div className={styles.contentContainer4}>
                        <div className={styles.leftContainer}>
                            <div
                                className={`${styles.mapContainer} ${styles.outline}`}
                            >
                                <TaskDetailMapPanel task={task} />
                            </div>
                            <div
                                className={`${styles.bottomLeftContainer} ${styles.outline}`}
                            >
                                <TaskSubjectInfo />
                                <div className={styles.lineSpacer} />

                                <TaskTeamInfo />
                            </div>
                        </div>
                        <div className={styles.rightContainer}>
                            <TaskEventsPanel task={task} />

                            <TaskBeaconSnapshotProvider task={task}>
                                <TaskLastLocationPanel />
                            </TaskBeaconSnapshotProvider>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
